// Voice Agent Homepage Data

export const agentCapabilities = [
  {
    title: "Listen",
    description: "Understand speech, intent, context and interruptions.",
    icon: "mic"
  },
  {
    title: "Reason",
    description: "Determine what needs to happen next.",
    icon: "brain"
  },
  {
    title: "Act",
    description: "Use tools, APIs, databases and enterprise applications.",
    icon: "zap"
  },
  {
    title: "Verify",
    description: "Determine whether the task was actually completed.",
    icon: "check"
  },
  {
    title: "Respond",
    description: "Communicate the right answer, action or next step naturally.",
    icon: "message"
  },
  {
    title: "Escalate",
    description: "Know when a human needs to intervene.",
    icon: "alert"
  },
  {
    title: "Remember",
    description: "Maintain context across interactions and workflows.",
    icon: "database"
  }
];

export const evolutionStages = [
  {
    stage: "IVR",
    description: '"Press 1..." "Press 2..." "Press 3..."',
    year: "1990s"
  },
  {
    stage: "Voice Bot",
    description: '"How can I help you?"',
    year: "2010s"
  },
  {
    stage: "Voice Agent",
    description: '"I\'ll take care of that."',
    year: "2020s"
  },
  {
    stage: "Vedara",
    description: '"I\'ll take care of it until it\'s done."',
    year: "Today"
  }
];

export const agentLifecycle = [
  {
    step: "01",
    title: "Understand",
    description: "Voice, intent, context"
  },
  {
    step: "02",
    title: "Reason",
    description: "Planning and decision making"
  },
  {
    step: "03",
    title: "Converse",
    description: "Natural human interaction"
  },
  {
    step: "04",
    title: "Act",
    description: "Tools, APIs and enterprise systems"
  },
  {
    step: "05",
    title: "Verify",
    description: "Evaluate whether the task succeeded"
  },
  {
    step: "06",
    title: "Continue",
    description: "Follow up, wait and resume"
  },
  {
    step: "07",
    title: "Escalate",
    description: "Human intervention when required"
  },
  {
    step: "08",
    title: "Complete",
    description: "Workflow successfully closed"
  }
];

export const benefitVerificationSteps = [
  { action: "CALL", description: "Automatically makes the outbound call." },
  { action: "NAVIGATE", description: "Handles IVRs and phone trees." },
  { action: "CONVERSE", description: "Speaks naturally with representatives." },
  { action: "EXTRACT", description: "Captures structured information." },
  { action: "REASON", description: "Validates coverage and determines what's missing." },
  { action: "INTEGRATE", description: "Writes the result back into the EHR/EMR." },
  { action: "ESCALATE", description: "Routes exceptions to humans." }
];

export const industries = [
  {
    title: "Healthcare & Life Sciences",
    workflows: [
      "Benefit Verification",
      "Prior Authorization",
      "Referral Management",
      "Patient Scheduling",
      "Provider Credentialing",
      "Patient Intake",
      "Claims & Payer Operations"
    ],
    cta: "Explore Healthcare"
  },
  {
    title: "Insurance",
    workflows: [
      "Claims Intake",
      "Claims Status",
      "Policy Servicing",
      "Underwriting Operations",
      "Collections",
      "Customer Retention",
      "Subrogation"
    ],
    cta: "Explore Insurance"
  },
  {
    title: "Financial Services",
    workflows: [
      "Loan Processing",
      "Customer Verification",
      "Collections",
      "Application Follow-up",
      "Account Servicing",
      "Customer Support"
    ],
    cta: "Explore Financial Services"
  },
  {
    title: "Enterprise",
    workflows: [
      "Customer Onboarding",
      "Vendor Management",
      "Appointment Scheduling",
      "Sales Development",
      "Back-office Operations",
      "Customer Support"
    ],
    cta: "Explore Enterprise"
  }
];

export const voiceAgentStack = [
  {
    layer: "01",
    title: "Agentic Intelligence",
    capabilities: [
      "Agent architectures",
      "Reasoning",
      "Multi-agent orchestration",
      "Tool use",
      "Context engineering",
      "Memory",
      "Long-horizon planning"
    ]
  },
  {
    layer: "02",
    title: "Voice Intelligence",
    capabilities: [
      "Speech recognition",
      "Voice synthesis",
      "Turn detection",
      "Interruption handling",
      "Latency optimization",
      "Conversational control"
    ]
  },
  {
    layer: "03",
    title: "Voice Infrastructure",
    capabilities: [
      "Telephony",
      "SIP",
      "Call routing",
      "IVR navigation",
      "Transfers",
      "Session management"
    ]
  },
  {
    layer: "04",
    title: "Enterprise Integrations",
    capabilities: [
      "EHR / EMR",
      "CRM",
      "Contact centers",
      "APIs",
      "Databases",
      "Enterprise applications"
    ]
  },
  {
    layer: "05",
    title: "Evaluation",
    capabilities: [
      "Task completion",
      "Conversation quality",
      "Accuracy",
      "Reliability",
      "Latency",
      "Safety",
      "Escalation"
    ]
  },
  {
    layer: "06",
    title: "Production",
    capabilities: [
      "Deployment",
      "Observability",
      "Security",
      "Compliance",
      "Monitoring",
      "Continuous optimization"
    ]
  }
];

export const howWeWork = [
  {
    step: "01",
    title: "Discover",
    description: "Find workflows with high volume, high friction and measurable economic value."
  },
  {
    step: "02",
    title: "Design",
    description: "Define the agent, tools, context, escalation and success criteria."
  },
  {
    step: "03",
    title: "Build",
    description: "Engineer the Voice Agent and enterprise integrations."
  },
  {
    step: "04",
    title: "Evaluate",
    description: "Test against real-world scenarios and edge cases."
  },
  {
    step: "05",
    title: "Deploy",
    description: "Launch into production."
  },
  {
    step: "06",
    title: "Optimize",
    description: "Continuously improve using production data."
  },
  {
    step: "07",
    title: "Scale",
    description: "Expand the agent across workflows, teams and geographies."
  }
];

export const vedaraBeliefs = [
  "Voice is the next interface for AI.",
  "Agents should execute, not just answer.",
  "The workflow matters more than the conversation.",
  "Production matters more than demos.",
  "Long-horizon tasks are where agents create disproportionate value.",
  "Enterprise integrations are part of the agent—not an afterthought.",
  "Every agent needs continuous evaluation.",
  "Customers should pay for outcomes, not hours."
];

export const longHorizonState = {
  objective: "Verify patient benefits",
  currentState: "Awaiting payer response",
  completed: ["Eligibility verified"],
  outstanding: ["Authorization requirement"],
  nextAction: "Call payer",
  escalationCondition: "Conflicting coverage information",
  successCriteria: "Benefits verified + EHR updated"
};
